import styles from '../../map.module.css';

import { useEffect, useState } from 'react';
import {
    ControlPosition,
    MapControl,
    useMap
} from '@vis.gl/react-google-maps';
import { MapControlTitle } from './components/MapControlTitle';
import { MapLoaderControl } from './components/MapLoaderControl';
import { MapControlZoom } from './components/MapControlZoom';
import { MapControlMultiSelect } from './components/MapControlMultiSelect';
import { MapControlFullscreen } from './components/MapControlFullscreen';
import { MapControlSearch } from './components/MapControlSearch';
import { MapControlSettings } from './components/MapControlSettings';
import { MapControlLegend } from './components/MapControlLegend';
import { MapControlAis } from './components/MapControlAis';
import { MapControlPilotCertificateCoastalSegments } from './components/MapControlPilotCertificateCoastalSegments';
import { waitForElm } from '../../../../helpers/ElementHelpers';
import { isNullOrEmpty} from '../../../../helpers/ObjectHelpers';
import { IMapParamsService } from '../../services/MapParamsService';
import { sleep } from '../../../../helpers/DataHelpers';
import {
    useMapId,
    useShowLoader,
    useMapTitle,
    useShowAis,
} from '../../hooks/useMapStore';

interface MapControlsProps {
    pilotageId?: number,
    shipId?: number,
    showSettings?: boolean,
    showSearch?: boolean,
    showMultiSelect?: boolean,
    pilotage?: any,
    pecExam?: any,
    paramsService: IMapParamsService,
    onMultiSelectCancelClick: () => void
    onMultiSelectOkClick: () => void,
}

export const MapControls = (
    {
        pilotageId,
        shipId,
        showSettings,
        showSearch,
        showMultiSelect,
        pecExam,
        pilotage,
        paramsService,
        onMultiSelectCancelClick,
        onMultiSelectOkClick,
    }: MapControlsProps) => {

    const map = useMap();

    const mapId = useMapId();
    const stateShowLoader = useShowLoader();
    const stateMapTitle = useMapTitle();
    const showAis = useShowAis();

    const [isFullscreen, setIsFulllscreen] = useState(false);
    const [title, setTitle] = useState("");
    const [showLoader, setShowLoader] = useState(false);

    const topLeftElemClassName = `googleMapControlTopLeft-${mapId}`;
    const bottomRightElemClassName = `googleMapControlBottomRight-${mapId}`;
    const countDownElemClassName = `googleMapControlCount-${mapId}`;

    const onSetPositions = (elemClassName:string, parentClassName:string) => {
        waitForElm(`.${elemClassName}`).then((elem:HTMLDivElement) => {
            elem.style.display = "grid";

            const parentElem = elem.parentElement;
            if (!parentElem) return;
            
            parentElem.classList.add(parentClassName);
        });
    }

    useEffect(() => {
        setShowLoader(stateShowLoader)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateShowLoader]);

    useEffect(() => {
        const handleMapTitleChanged = async (data: string | null) => {
            setTitle("");
            if (isNullOrEmpty(data)) return;
            sleep(100);
            setTitle(data!);
        }
        handleMapTitleChanged(stateMapTitle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateMapTitle]);

    useEffect(() => {
        const initialize = () => {
            waitForElm(`.${countDownElemClassName}`).then((elem: HTMLDivElement) => {
                const parentElem = elem.parentElement;
                if (!parentElem) return;

                parentElem.classList.add("gmc-cd");
            });
        }

        initialize();
    }, []);

    useEffect(() => {
        if (!map) return;

        onSetPositions(topLeftElemClassName, "gmc-tl");
        onSetPositions(bottomRightElemClassName, "gmc-br");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    useEffect(() => {
        if (!map) return;
        if (pilotageId === 0 && shipId === 0 ) return;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, pilotageId, shipId]);

    return (
        <>
            <MapControl position={ControlPosition.TOP_LEFT}>
                <div className={`${styles.controlsTopLeft} ${topLeftElemClassName}`}>
                    <div>
                        <MapControlSettings show={showSettings} />
                        {
                            (showAis) &&
                            <MapControlAis />
                        }
                    </div>
                    <MapControlSearch show={showSearch} />
                    <MapControlPilotCertificateCoastalSegments />
                </div>
            </MapControl>

            <MapControl position={ControlPosition.TOP_LEFT}>
                <div className={`gmc-cd-c ${countDownElemClassName}`}>
                    
                </div>
            </MapControl>

            <MapControl position={ControlPosition.TOP_RIGHT}>
                <MapControlFullscreen
                    setIsFulllscreen={setIsFulllscreen}
                    isFullscreen={isFullscreen} />
            </MapControl>

            <MapControl position={ControlPosition.CENTER}>
                <MapLoaderControl show={showLoader} />
            </MapControl>

            <MapControl position={ControlPosition.BOTTOM_LEFT}>
                <MapControlLegend
                    pilotage={pilotage}
                    pecExam={pecExam}
                    paramsService={paramsService}
                />
            </MapControl>

            {
                (!isNullOrEmpty(title)) &&
                <MapControl position={ControlPosition.BOTTOM_CENTER}>
                    <MapControlTitle title={title} />
                </MapControl>
            }

            <MapControl position={ControlPosition.BOTTOM_RIGHT}>
                <div className={`k-d-grid k-gap-2 ${bottomRightElemClassName}`}>
                    <MapControlMultiSelect
                        show={showMultiSelect}
                        onOkClick={onMultiSelectOkClick}
                        onCancelClick={onMultiSelectCancelClick}
                    />
                    <MapControlZoom />
                </div>
            </MapControl>
        </>
    );
}
